import { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react'
import { authService } from '@services/authService'
import api from '@services/api'
import { auth } from '@services/firebase' // NEW: Import auth instance
import { signInWithCustomToken, signOut as firebaseSignOut } from 'firebase/auth' // NEW: Import signInWithCustomToken and rename signOut to firebaseSignOut
import { useToast } from './ToastContext'
import storage from '@services/storage'

const AuthContext = createContext(null)

// Status de cadastro
export const REGISTRATION_STATUS = {
  NONE: 'none',           // Não cadastrado
  BASIC: 'basic',         // Cadastro básico (email, nome, senha)
  COMPLETE: 'complete',   // Cadastro completo (com CPF, telefone, etc)
  VERIFIED: 'verified'    // Verificado (já entrou em evento)
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const token = storage.getItem('auth_token')
    const storedUser = authService.getStoredUser()
    return token && storedUser ? storedUser : null
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [loginMethod, setLoginMethodState] = useState(() => storage.getItem('login_method')) // New state for login method
  const [resendCooldown, setResendCooldown] = useState(0) // Tempo restante em segundos
  const [isResendingEmail, setIsResendingEmail] = useState(false)
  const { showToast } = useToast()

  // Helper to set login method in state and storage
  const setLoginMethodAndStorage = useCallback((method) => {
    setLoginMethodState(method)
    if (method) {
      storage.setItem('login_method', method)
    } else {
      storage.removeItem('login_method')
    }
  }, [])

  // Helper function to remove undefined, null, and empty string values from an object
  const cleanObject = useCallback((obj) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value
      }
      return acc
    }, {})
  }, [])

  // Gerencia o timer do cooldown
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown(prev => Math.max(0, prev - 1));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Carrega usuário, loginMethod e cooldown do storage na inicialização
  useEffect(() => {
    const loadUser = async () => {
      const token = storage.getItem('auth_token')
      const storedUser = authService.getStoredUser()
      const storedLoginMethod = storage.getItem('login_method') // Load login method
      
      // Verifica cooldown pendente
      const lastResend = storage.getItem('last_email_resend_ts');
      if (lastResend) {
        const diff = Math.floor((Date.now() - parseInt(lastResend)) / 1000);
        const remaining = 300 - diff; // 5 minutos = 300 segundos
        if (remaining > 0) {
          setResendCooldown(remaining);
        }
      }

      if (token && storedUser) {
        setUser(storedUser)
        setLoginMethodState(storedLoginMethod) // Set login method state
        
        // Tenta atualizar dados do usuário do servidor
        try {
          const response = await api.get('/me/profile')
          const updatedUser = response
          setUser(updatedUser)
          authService.setStoredUser(updatedUser)
        } catch (err) {
          console.error('Erro ao carregar perfil do usuário:', err);
          showToast(err.message || 'Erro ao carregar suas informações de perfil.', 'error');
        }
      }
      
      setLoading(false)
    }

    loadUser()

    // Escuta evento de logout forçado (token expirado)
    const handleLogout = async () => {
      const hadSession = !!storage.getItem('auth_token') || !!authService.getStoredUser()
      setUser(null)
      setLoginMethodAndStorage(null) // Clear login method on logout
      authService.logout()
      try {
        await firebaseSignOut(auth);
      } catch (fbErr) {
        console.error('Erro ao fazer logout do Firebase Auth durante logout forçado:', fbErr);
      }
      if (hadSession) {
        showToast('Sua sessão expirou por segurança. Faça login novamente.', 'info');
      }
    }

    window.addEventListener('auth:logout', handleLogout)
    return () => window.removeEventListener('auth:logout', handleLogout)
  }, [setLoginMethodAndStorage, showToast])

  // Login com email e senha
  const signIn = useCallback(async (email, password) => {
    setError(null)
    setLoading(true)
    
    try {
      const loginResponse = await api.post('/auth/login', { email, password })
      
      if (loginResponse.tokens && loginResponse.tokens.access_token && loginResponse.tokens.access_token.length > 0) {
        storage.setItem('auth_token', loginResponse.tokens.access_token)
        if (loginResponse.tokens.refresh_token && loginResponse.tokens.refresh_token.length > 0) {
          storage.setItem('refresh_token', loginResponse.tokens.refresh_token)
        }
        
        // NEW: Handle Firebase Custom Token only if it's not empty
        if (loginResponse.tokens.firebase_custom_token && loginResponse.tokens.firebase_custom_token.length > 0) {
          await signInWithCustomToken(auth, loginResponse.tokens.firebase_custom_token)
          console.log('Firebase Auth signed in with custom token.')
        } else {
          console.warn('Firebase custom token is empty or null, proceeding without Firebase Auth.')
        }

      } else {
        throw new Error('Token de autenticação não recebido ou inválido.')
      }

      const userProfile = loginResponse.user
      setUser(userProfile)
      authService.setStoredUser(userProfile)
      setLoginMethodAndStorage('credentials') // Set login method to credentials
      
      return { success: true, user: userProfile }
    } catch (err) {
      const message = err.message || 'E-mail ou senha incorretos'
      setError(message)
      showToast(message, 'error')
      authService.logout() // Limpa tokens e usuário em caso de falha
      // Also sign out from Firebase Auth if it was a custom token issue
      try {
        await firebaseSignOut(auth);
      } catch (fbErr) {
        console.error('Erro ao fazer logout do Firebase Auth durante falha de login:', fbErr);
      }
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }, [showToast, setLoginMethodAndStorage])

  // Cadastro básico com email, nome e senha
  const signUp = useCallback(async (userData) => {
    setError(null)
    setLoading(true)
    
    try {
      const registerResponse = await api.post('/auth/register', {
        cep: userData.cep,
        address: userData.address,
        slug_city: userData.slug_city,
        email: userData.email,
        password: userData.password,
        main_document: userData.main_document,
        document_type: userData.document_type,
        ...(userData.document_type !== 'CPF' && { name: userData.name }),
        ...(userData.document_type !== 'CPF' && { birth_date: userData.birth_date })
      })
      
      if (registerResponse.tokens && registerResponse.tokens.access_token && registerResponse.tokens.access_token.length > 0) {
        storage.setItem('auth_token', registerResponse.tokens.access_token)
        if (registerResponse.tokens.refresh_token && registerResponse.tokens.refresh_token.length > 0) {
          storage.setItem('refresh_token', registerResponse.tokens.refresh_token)
        }

        // NEW: Handle Firebase Custom Token only if it's not empty
        if (registerResponse.tokens.firebase_custom_token && registerResponse.tokens.firebase_custom_token.length > 0) {
          await signInWithCustomToken(auth, registerResponse.tokens.firebase_custom_token)
          console.log('Firebase Auth signed in with custom token on signup.')
        } else {
          console.warn('Firebase custom token is empty or null, proceeding without Firebase Auth.')
        }

      } else {
        throw new Error('Token de autenticação não recebido após o cadastro ou inválido.')
      }
      
      const userProfile = registerResponse.user
      if (!userProfile) {
        throw new Error('Dados do usuário não recebidos após o cadastro.')
      }

      setUser(userProfile)
      authService.setStoredUser(userProfile)
      setLoginMethodAndStorage('credentials') // Set login method to credentials
      
      return { success: true, user: userProfile }
    } catch (err) {
      const message = err.message || 'Erro ao criar conta'
      setError(message)
      showToast(message, 'error')
      authService.logout() // Limpa tokens e usuário em caso de falha
      // Also sign out from Firebase Auth if it was a custom token issue
      try {
        await firebaseSignOut(auth);
      } catch (fbErr) {
        console.error('Erro ao fazer logout do Firebase Auth durante falha de cadastro:', fbErr);
      }
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }, [showToast, setLoginMethodAndStorage])

  // Login com Google
  const signInWithGoogle = useCallback(() => {
    // Redireciona para o endpoint de login do Google
    const googleLoginUrl = authService.getGoogleLoginUrl()
    window.location.href = googleLoginUrl
  }, [])

  // Callback do Google OAuth
  const handleGoogleCallback = useCallback(async (accessToken, firebaseToken) => {
    setError(null)
    setLoading(true)
    
    try {
      if (!accessToken || !firebaseToken) {
        throw new Error('Tokens de autenticação do Google não recebidos.')
      }

      storage.setItem('auth_token', accessToken)
      storage.removeItem('refresh_token') // Assuming no refresh token is provided in this flow

      await signInWithCustomToken(auth, firebaseToken)
      console.log('Firebase Auth signed in with custom token from Google callback.')

      // Fetch user profile from backend
      const userProfile = await api.get('/me/profile') // Assuming this endpoint returns the full user object

      if (!userProfile) {
        throw new Error('Dados do usuário não recebidos após o login com Google.')
      }

      setUser(userProfile)
      authService.setStoredUser(userProfile)
      setLoginMethodAndStorage('google') // Set login method to google

      // Clear the URL parameters to prevent re-processing and cleaner URL
      window.history.replaceState({}, document.title, window.location.pathname)
      
      return { success: true, user: userProfile }
    } catch (err) {
      const message = err.message || 'Erro ao fazer login com Google'
      setError(message)
      showToast(message, 'error')
      authService.logout() // Clean up tokens and user on failure
      try {
        await firebaseSignOut(auth)
      } catch (fbErr) {
        console.error('Erro ao fazer logout do Firebase Auth durante falha de Google login:', fbErr)
      }
      // Clear URL parameters even on error
      window.history.replaceState({}, document.title, window.location.pathname)
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Completar cadastro com informações adicionais
  const completeRegistration = useCallback(async (newData) => {
    setError(null)
    setLoading(true)
    
    try {
      const cleanedData = cleanObject(newData)
      const response = await api.put('/me/profile', cleanedData)
      
      const updatedUser = {
        ...user,
        ...newData,
        ...response
      }

      // Re-evaluate registration status based on all available fields
      const isPersonalComplete = updatedUser.main_document && updatedUser.birth_date;
      const isAddressComplete = updatedUser.cep && updatedUser.address; // Assuming 'address' is the concatenated string

      if (isPersonalComplete && isAddressComplete) {
        updatedUser.registrationStatus = REGISTRATION_STATUS.COMPLETE;
      } else if (isPersonalComplete) {
        // User has completed personal but not address
        updatedUser.registrationStatus = REGISTRATION_STATUS.BASIC; // Or a new status like 'ADDRESS_PENDING'
      } else {
        updatedUser.registrationStatus = REGISTRATION_STATUS.NONE;
      }

      setUser(updatedUser)
      authService.setStoredUser(updatedUser)
      
      return { success: true, user: updatedUser }
    } catch (err) {
      const message = err.message || 'Erro ao completar cadastro'
      setError(message)
      showToast(message, 'error')
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }, [user, showToast, cleanObject])

  const refreshProfile = useCallback(async () => {
    try {
      const response = await api.get('/me/profile')
      const updatedUser = response
      setUser(updatedUser)
      authService.setStoredUser(updatedUser)
      return updatedUser
    } catch (err) {
      console.error('Erro ao atualizar perfil do usuário:', err);
      throw err;
    }
  }, []);

  // Logout
  const signOut = useCallback(async () => {
    try {
      authService.logout()
      setUser(null)
      // NEW: Sign out from Firebase Auth
      await firebaseSignOut(auth);
      return { success: true }
    } catch (err) {
      console.error('Erro ao fazer logout:', err);
      showToast('Erro ao sair', 'error'); // Display error toast
      return { success: false, error: 'Erro ao sair' }
    }
  }, [showToast])

  // Recuperação de senha
  const resetPassword = useCallback(async (email) => {
    setError(null)
    
    try {
      await api.post('/auth/forgot-password', { email })
      return { success: true }
    } catch (err) {
      const message = err.message || 'Erro ao enviar e-mail de recuperação'
      setError(message)
      showToast(message, 'error') // Display error toast
      return { success: false, error: message }
    }
  }, [showToast])

  // Reenvia e-mail de validação
  const resendVerificationEmail = useCallback(async (slugCity) => {
    if (resendCooldown > 0 || isResendingEmail) return { success: false, error: 'Aguarde' };

    setIsResendingEmail(true)
    try {
      await api.get('/validate-email', { slug_city: slugCity })
      showToast('E-mail enviado! Confira sua caixa de entrada e spam.', 'success')
      
      // Inicia cooldown de 5 minutos
      const now = Date.now();
      storage.setItem('last_email_resend_ts', now.toString());
      setResendCooldown(300);
      
      return { success: true }
    } catch (err) {
      const message = err.message || 'Erro ao enviar e-mail de validação'
      showToast(message, 'error')
      return { success: false, error: message }
    } finally {
      setIsResendingEmail(false)
    }
  }, [showToast, resendCooldown, isResendingEmail])

  // Valida o e-mail com o token recebido
  const verifyEmail = useCallback(async (token) => {
    try {
      await api.post('/auth/validate-email', { token })
      showToast('E-mail verificado com sucesso!', 'success')
      
      // Se o usuário estiver logado, atualiza o perfil para refletir a verificação
      if (storage.getItem('auth_token')) {
        try {
          const updatedUser = await api.get('/me/profile')
          setUser(updatedUser)
          authService.setStoredUser(updatedUser)
        } catch (e) {
          console.error('Erro ao atualizar perfil após verificação:', e)
        }
      }
      
      return { success: true }
    } catch (err) {
      const message = err.message || 'Erro ao verificar e-mail. O token pode ter expirado.'
      showToast(message, 'error')
      return { success: false, error: message }
    }
  }, [showToast])

  // Recuperação de senha (novo fluxo em duas etapas)
  const recoverPassword = useCallback(async (mainDocument, email, slugCity) => {
    setError(null)
    setLoading(true)
    
    try {
      await api.post('/users/password-recovery', { 
        main_document: mainDocument, 
        email,
        slug_city: slugCity
        })
        showToast('E-mail de recuperação enviado! Confira sua caixa de entrada e spam.', 'success')
        return { success: true }    } catch (err) {
      const message = err.message || 'Erro ao processar recuperação de senha'
      setError(message)
      showToast(message, 'error')
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Atualiza perfil do usuário
  const updateUserProfile = useCallback(async (data) => {
    if (!user) return { success: false, error: 'Não autenticado' }
    
    try {
      const cleanedData = cleanObject(data)
      const response = await api.put('/me/profile', cleanedData)
      
      const updatedUser = { ...user, ...response }
      setUser(updatedUser)
      authService.setStoredUser(updatedUser)
      
      return { success: true, user: updatedUser }
    } catch (err) {
      const message = err.message || 'Erro ao atualizar perfil';
      showToast(message, 'error');
      return { success: false, error: message }
    }
  }, [user, showToast, cleanObject])

  // Altera a senha do usuário
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setError(null)
    setLoading(true)
    
    try {
      const response = await api.post('/auth/reset-password', { 
        current_password: currentPassword, 
        new_password: newPassword 
      })
      
      // Se a alteração for bem sucedida, o backend deve retornar o usuário atualizado
      // ou pelo menos indicar que has_temporary_password agora é false.
      const updatedUser = { 
        ...user, 
        has_temporary_password: false,
        ...response.user 
      }
      
      setUser(updatedUser)
      authService.setStoredUser(updatedUser)
      
      showToast('Senha alterada com sucesso!', 'success')
      return { success: true }
    } catch (err) {
      let message = err.message || 'Erro ao alterar senha'
      
      // Se for 401 e a mensagem for genérica, provavelmente é a senha atual/temporária incorreta
      if (err.status === 401 && (message === 'Unauthorized' || message === 'Unauthorized.' || message === 'Erro desconhecido')) {
        message = user?.has_temporary_password ? 'A senha temporária informada está incorreta' : 'A senha atual informada está incorreta'
      }

      setError(message)
      showToast(message, 'error')
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }, [user, showToast])

  // Verifica se o cadastro está completo
    const isRegistrationComplete = Boolean(user?.main_document && 
                                            user?.birth_date && 
                                            user?.cep && 
                                            user?.address);

  // Novos status do usuário
  const isEmailVerified = user?.is_email_verified === true;
  const hasTemporaryPassword = user?.has_temporary_password === true;

  // Verifica se o usuário é verificado (já entrou em evento)
  const isVerified = user?.registrationStatus === REGISTRATION_STATUS.VERIFIED

  // Verifica se pode comprar ingressos (precisa cadastro completo, email verificado e senha definitiva)
  const canPurchaseTickets = isRegistrationComplete && isEmailVerified && !hasTemporaryPassword

  // Verifica se pode reagir/confirmar presença (precisa de um ID de usuário)
  const canInteract = !!user?.id
  // Memoize context value to avoid unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    error,
    isAuthenticated: !!user?.id,
    isRegistrationComplete,
    isEmailVerified,
    hasTemporaryPassword,
    isVerified,
    canPurchaseTickets,
    canInteract,
    loginMethod,
    resendCooldown,
    isResendingEmail,
    signIn,
    signUp,
    signInWithGoogle,
    handleGoogleCallback,
    completeRegistration,
    signOut,
    resetPassword,
    recoverPassword,
    resendVerificationEmail,
    verifyEmail,
    updateUserProfile,
    refreshProfile,
    changePassword,
    clearError: () => setError(null),
    REGISTRATION_STATUS
  }), [
    user, 
    loading, 
    error, 
    isRegistrationComplete, 
    isEmailVerified,
    hasTemporaryPassword,
    isVerified, 
    canPurchaseTickets, 
    canInteract, 
    loginMethod, 
    resendCooldown,
    isResendingEmail,
    signIn, 
    signUp, 
    signInWithGoogle, 
    handleGoogleCallback, 
    completeRegistration, 
    signOut, 
    resetPassword, 
    recoverPassword,
    resendVerificationEmail,
    verifyEmail,
    updateUserProfile,
    changePassword
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider')
  }
  
  return context
}

export default AuthContext
